export const HEALTH_SOURCES_LAST_UPDATED = 'May 27, 2026';

export const HEALTH_SOURCE_REFERENCES = [
  {
    id: 'ACT-WHO',
    title: 'WHO Physical Activity Fact Sheet',
    label: 'WHO',
    href: 'https://www.who.int/news-room/fact-sheets/detail/physical-activity',
    summary: 'World Health Organization guidance on adult physical activity and its role in reducing health risks.'
  },
  {
    id: 'ACT-CDC',
    title: 'CDC Adult Activity Guidelines',
    label: 'CDC',
    href: 'https://www.cdc.gov/physical-activity-basics/guidelines/adults.html',
    summary: 'Centers for Disease Control and Prevention overview of weekly aerobic and muscle-strengthening activity targets for adults.'
  },
  {
    id: 'EXR-MLP',
    title: 'MedlinePlus Benefits of Exercise',
    label: 'MedlinePlus',
    href: 'https://medlineplus.gov/benefitsofexercise.html',
    summary: 'National Library of Medicine reference page covering the broad health benefits of exercise and muscle-strengthening activity.'
  },
  {
    id: 'SLP-CDC',
    title: 'CDC About Sleep',
    label: 'CDC',
    href: 'https://www.cdc.gov/sleep/about/index.html',
    summary: 'CDC sleep-health overview, including the adult recommendation for 7 or more hours of sleep per night.'
  },
  {
    id: 'SLP-NHLBI',
    title: 'NHLBI Healthy Sleep Habits',
    label: 'NHLBI',
    href: 'https://www.nhlbi.nih.gov/health/sleep-deprivation/healthy-sleep-habits',
    summary: 'National Heart, Lung, and Blood Institute guidance on sleep habits such as schedule consistency and sleep environment.'
  },
  {
    id: 'NUT-EFSA-DRV',
    title: 'EFSA Dietary Reference Values',
    label: 'EFSA',
    href: 'https://www.efsa.europa.eu/en/topics/topic/dietary-reference-values',
    summary: 'European Food Safety Authority overview of dietary reference values for energy and nutrients across life stages.'
  },
  {
    id: 'NUT-EFSA-PRO',
    title: 'EFSA Protein Dietary Reference Values',
    label: 'EFSA',
    href: 'https://www.efsa.europa.eu/en/efsajournal/pub/2557',
    summary: 'EFSA scientific opinion on protein dietary reference values and the role of protein intake in adult nutrition.'
  },
  {
    id: 'DIET-WHO',
    title: 'WHO Healthy Diet Fact Sheet',
    label: 'WHO',
    href: 'https://www.who.int/news-room/fact-sheets/detail/healthy-diet',
    summary: 'WHO guidance on healthy eating patterns, sugar, salt, fat quality, and variety across food groups.'
  },
  {
    id: 'PRO-MLP',
    title: 'MedlinePlus Dietary Proteins',
    label: 'MedlinePlus',
    href: 'https://medlineplus.gov/dietaryproteins.html',
    summary: 'MedlinePlus overview of dietary protein, food sources, and protein’s role in the body.'
  },
  {
    id: 'CARB-MLP',
    title: 'MedlinePlus Carbohydrates',
    label: 'MedlinePlus',
    href: 'https://medlineplus.gov/carbohydrates.html',
    summary: 'MedlinePlus overview of carbohydrate basics, including carbohydrate-rich foods and glucose as a fuel source.'
  },
  {
    id: 'FIB-MLP',
    title: 'MedlinePlus Fiber',
    label: 'MedlinePlus',
    href: 'https://medlineplus.gov/ency/article/002470.htm',
    summary: 'MedlinePlus medical encyclopedia entry on dietary fiber and digestive-health context.'
  },
  {
    id: 'HYD-MLP',
    title: 'MedlinePlus Water in Diet',
    label: 'MedlinePlus',
    href: 'https://medlineplus.gov/ency/article/002471.htm',
    summary: 'MedlinePlus medical encyclopedia entry on water intake and the role of fluids from foods and beverages.'
  },
  {
    id: 'FALL-CDC',
    title: 'CDC Fall Prevention',
    label: 'CDC',
    href: 'https://www.cdc.gov/falls/prevention/index.html',
    summary: 'CDC guidance on preventing falls, including the role of strength and balance exercises for older adults.'
  },
];

export const FORZA_DEFAULTS = [
  {
    id: 'DEFAULT-STEPS',
    title: 'Daily step quest target',
    currentValue: '10,000 steps',
    summary: 'Forza sets a daily step quest at 10,000 steps as a general activity goal, inspired by broad physical-activity guidance. This is a product default, not a medical threshold.',
    refIds: ['ACT-WHO', 'ACT-CDC'],
  },
  {
    id: 'DEFAULT-SLEEP',
    title: 'Daily sleep quest target',
    currentValue: '7 hours',
    summary: 'Forza uses 7 hours as its sleep quest target, drawing on general adult sleep recommendations. This is a product default.',
    refIds: ['SLP-CDC', 'SLP-NHLBI'],
  },
  {
    id: 'DEFAULT-ACTIVITY-MULTIPLIERS',
    title: 'Activity multipliers for calorie planning',
    currentValue: '1.20 to 1.60',
    summary: 'Forza estimates your daily calorie needs based on how active you say you are. The multipliers (1.20-1.60) are planning assumptions, not clinical calculations.',
    refIds: ['ACT-CDC', 'NUT-EFSA-DRV'],
  },
  {
    id: 'DEFAULT-CALORIE-ADJUSTMENT',
    title: 'Goal-based calorie adjustment logic',
    currentValue: '-12%, 0%, +12%',
    summary: 'Forza nudges estimated maintenance calories down for fat loss, leaves them flat for maintenance, and nudges them up for muscle gain. These percentages are product defaults.',
    refIds: ['DIET-WHO', 'NUT-EFSA-DRV'],
  },
  {
    id: 'DEFAULT-PROTEIN',
    title: 'Protein targets in plan generation',
    currentValue: '1.6 to 1.8 g/kg',
    summary: 'Forza sets protein targets at 1.6-1.8 g/kg for training-focused plans. These are product defaults chosen for active users, not universal dietary prescriptions.',
    refIds: ['NUT-EFSA-PRO', 'PRO-MLP'],
  },
  {
    id: 'DEFAULT-FAT',
    title: 'Fat default in macro planning',
    currentValue: '0.8 g/kg',
    summary: 'Forza uses a simple per-kilogram fat default to keep meal plans computationally stable. The number is a product planning choice.',
    refIds: ['NUT-EFSA-DRV', 'DIET-WHO'],
  },
  {
    id: 'DEFAULT-MEAL-SPLIT',
    title: 'Meal calorie split',
    currentValue: '30% / 35% / 35%',
    summary: 'Forza divides daily calories across breakfast, lunch, and dinner using a fixed split for plan generation convenience. This is not clinical advice.',
    refIds: ['DIET-WHO', 'NUT-EFSA-DRV'],
  },
  {
    id: 'DEFAULT-CARB-CAPS',
    title: 'Keto and low-carb caps',
    currentValue: 'Keto <= 50 g, low-carb ~= 20% of calories',
    summary: 'Forza applies internal caps when users choose keto or low-carb preferences. These caps are app defaults used to shape generated meals.',
    refIds: ['CARB-MLP', 'DIET-WHO'],
  },
  {
    id: 'DEFAULT-CALORIE-FLOOR',
    title: 'Minimum calorie floor',
    currentValue: '1200 kcal',
    summary: 'Forza never generates a plan below 1,200 kcal per day. This minimum is a built-in safety limit, not a personalized recommendation.',
    refIds: ['DIET-WHO', 'NUT-EFSA-DRV'],
  },
];

const ACTIVITY_FACTS = [
  { text: 'Adults benefit from regular physical activity.', refIds: ['ACT-WHO', 'ACT-CDC'] },
  { text: 'According to CDC guidelines, adults can support their health with at least 150 minutes of moderate-intensity aerobic activity each week.', refIds: ['ACT-CDC'] },
  { text: 'CDC guidelines note that 75 minutes of vigorous-intensity activity per week is an alternative way to meet aerobic targets.', refIds: ['ACT-CDC'] },
  { text: 'You can mix moderate and vigorous activity across the week - both count toward recommended movement targets.', refIds: ['ACT-CDC'] },
  { text: 'Health guidelines recommend muscle-strengthening activity on 2 or more days each week.', refIds: ['ACT-CDC', 'ACT-WHO'] },
  { text: 'Some physical activity is better than none.', refIds: ['ACT-WHO', 'ACT-CDC'] },
  { text: 'Moving more and sitting less benefits adults.', refIds: ['ACT-CDC'] },
  { text: 'Regular physical activity helps reduce the risk of cardiovascular disease.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Regular physical activity helps reduce the risk of type 2 diabetes.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Regular physical activity helps reduce the risk of hypertension.', refIds: ['ACT-WHO'] },
  { text: 'Regular physical activity helps reduce the risk of some cancers.', refIds: ['ACT-WHO'] },
  { text: 'Physical activity can reduce symptoms of anxiety.', refIds: ['ACT-WHO'] },
  { text: 'Physical activity can reduce symptoms of depression.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Physical activity supports brain health.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Physical activity can improve overall well-being.', refIds: ['ACT-WHO'] },
  { text: 'Physical activity can improve sleep.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Physical activity can help reduce body fat.', refIds: ['ACT-WHO', 'EXR-MLP'] },
  { text: 'Muscle-strengthening activities can help increase or maintain muscle mass.', refIds: ['ACT-CDC', 'EXR-MLP'] },
  { text: 'Muscle-strengthening activities can help increase or maintain strength.', refIds: ['ACT-CDC', 'EXR-MLP'] },
  { text: 'Walking counts as physical activity.', refIds: ['ACT-CDC', 'EXR-MLP'] },
  { text: 'Activity can be spread across the week instead of done all at once.', refIds: ['ACT-CDC'] },
  { text: 'Shorter bouts of movement still contribute to weekly activity totals.', refIds: ['ACT-CDC'] },
  { text: 'Doing more than the minimum activity target can bring additional health benefits.', refIds: ['ACT-CDC', 'ACT-WHO'] },
  { text: 'Physical activity helps with prevention and management of noncommunicable diseases.', refIds: ['ACT-WHO'] },
  { text: 'Exercise can help improve fitness.', refIds: ['EXR-MLP'] },
  { text: 'Exercise can help improve mood.', refIds: ['EXR-MLP'] },
  { text: 'Exercise can help with weight management when combined with nutrition habits.', refIds: ['EXR-MLP'] },
  { text: 'Adults aged 65 and older should include balance-challenging activity if they are at risk of falls.', refIds: ['ACT-WHO', 'FALL-CDC'] },
  { text: 'Strength and balance exercises are part of fall-prevention guidance for older adults.', refIds: ['FALL-CDC'] },
  { text: 'Regular movement can support healthier aging.', refIds: ['ACT-WHO', 'EXR-MLP'] },
];

const SLEEP_FACTS = [
  { text: 'Healthy adults generally need 7 or more hours of sleep per night.', refIds: ['SLP-CDC'] },
  { text: 'Sleep needs vary by age.', refIds: ['SLP-CDC'] },
  { text: 'Getting enough sleep supports better health.', refIds: ['SLP-CDC', 'SLP-NHLBI'] },
  { text: 'Sleeping too little can affect how you function during the day.', refIds: ['SLP-CDC', 'SLP-NHLBI'] },
  { text: 'Keeping a consistent sleep schedule is a healthy sleep habit.', refIds: ['SLP-NHLBI'] },
  { text: 'Going to bed and waking up at similar times each day can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'A quiet bedroom can support better sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'A dark bedroom can support better sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'A cool bedroom can support better sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'Turning off electronic devices before bed can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'A relaxing bedtime routine can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'Avoiding caffeine close to bedtime can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'Avoiding nicotine close to bedtime can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'Avoiding alcohol close to bedtime can support sleep.', refIds: ['SLP-NHLBI'] },
  { text: 'Getting physical activity during the day can support sleep.', refIds: ['SLP-NHLBI', 'ACT-WHO'] },
  { text: 'Allowing enough time for sleep is part of healthy sleep habits.', refIds: ['SLP-NHLBI'] },
  { text: 'Good sleep supports brain performance.', refIds: ['SLP-NHLBI', 'SLP-CDC'] },
  { text: 'Good sleep supports mood.', refIds: ['SLP-NHLBI', 'SLP-CDC'] },
  { text: 'Good sleep supports overall health.', refIds: ['SLP-NHLBI', 'SLP-CDC'] },
  { text: 'Sleep health is part of overall health, not a separate extra.', refIds: ['SLP-CDC', 'SLP-NHLBI'] },
];

const NUTRITION_FACTS = [
  { text: 'A healthy diet helps support health and well-being.', refIds: ['DIET-WHO'] },
  { text: 'Eating a variety of foods helps cover nutrient needs.', refIds: ['DIET-WHO', 'NUT-EFSA-DRV'] },
  { text: 'Fruits and vegetables are part of a healthy diet pattern.', refIds: ['DIET-WHO'] },
  { text: 'Pulses and whole grains are part of a healthy diet pattern.', refIds: ['DIET-WHO'] },
  { text: 'Lean sources of protein can be part of a healthy diet pattern.', refIds: ['DIET-WHO', 'PRO-MLP'] },
  { text: 'WHO advises at least 400 grams of fruit and vegetables per day.', refIds: ['DIET-WHO'] },
  { text: 'Limiting free sugars is part of WHO healthy diet guidance.', refIds: ['DIET-WHO'] },
  { text: 'WHO recommends keeping free sugars below 10% of total energy intake.', refIds: ['DIET-WHO'] },
  { text: 'WHO notes that reducing free sugars below 5% of energy intake may bring additional benefits.', refIds: ['DIET-WHO'] },
  { text: 'WHO recommends less than 5 grams of salt per day for adults.', refIds: ['DIET-WHO'] },
  { text: 'WHO recommends keeping total fat below 30% of total energy intake.', refIds: ['DIET-WHO'] },
  { text: 'WHO recommends keeping saturated fat below 10% of total energy intake.', refIds: ['DIET-WHO'] },
  { text: 'WHO recommends keeping trans-fat below 1% of total energy intake.', refIds: ['DIET-WHO'] },
  { text: 'Replacing saturated fat with unsaturated fat is part of WHO healthy diet guidance.', refIds: ['DIET-WHO'] },
  { text: 'Cutting industrial trans-fat helps support a healthier diet pattern.', refIds: ['DIET-WHO'] },
  { text: 'Protein is one of the nutrients covered by EFSA dietary reference values.', refIds: ['NUT-EFSA-DRV', 'NUT-EFSA-PRO'] },
  { text: 'EFSA publishes reference values for nutrients that provide energy - protein, carbohydrates, and fats.', refIds: ['NUT-EFSA-DRV'] },
  { text: 'Protein contributes to the growth of muscle mass.', refIds: ['NUT-EFSA-PRO'] },
  { text: 'Protein contributes to the maintenance of muscle mass.', refIds: ['NUT-EFSA-PRO'] },
  { text: 'Protein contributes to the maintenance of normal bones.', refIds: ['NUT-EFSA-PRO'] },
  { text: 'Protein foods can come from both animal and plant sources.', refIds: ['PRO-MLP'] },
  { text: 'Meat, dairy products, nuts, and certain grains and beans can all provide dietary protein.', refIds: ['PRO-MLP'] },
  { text: 'Carbohydrates are one of the main nutrients in the diet.', refIds: ['CARB-MLP'] },
  { text: 'The body breaks many carbohydrates down into glucose.', refIds: ['CARB-MLP'] },
  { text: 'Glucose is a main source of energy for the body’s cells, tissues, and organs.', refIds: ['CARB-MLP'] },
  { text: 'Fiber is a type of carbohydrate.', refIds: ['CARB-MLP', 'FIB-MLP'] },
  { text: 'Fiber adds bulk to the diet.', refIds: ['FIB-MLP'] },
  { text: 'Fiber is part of digestive-health guidance in MedlinePlus.', refIds: ['FIB-MLP'] },
  { text: 'Water is an essential nutrient - the body needs it every day for normal function.', refIds: ['HYD-MLP'] },
  { text: 'Daily water intake comes from both foods and beverages.', refIds: ['HYD-MLP'] },
  { text: 'Getting enough fluids helps the body maintain normal water balance.', refIds: ['HYD-MLP'] },
  { text: 'Dehydration happens when the body does not have enough water or other fluids.', refIds: ['HYD-MLP'] },
  { text: 'Nutrition includes protein, carbohydrates, fats, vitamins, minerals, and water.', refIds: ['NUT-EFSA-DRV', 'HYD-MLP'] },
  { text: 'A balanced diet comes from variety - no single food covers all your nutritional needs.', refIds: ['DIET-WHO'] },
  { text: 'Food variety can help reduce gaps in vitamins and minerals.', refIds: ['DIET-WHO', 'NUT-EFSA-DRV'] },
  { text: 'Fruits, vegetables, legumes, nuts, and whole grains are core parts of WHO healthy diet guidance.', refIds: ['DIET-WHO'] },
  { text: 'Highly processed foods can make healthy eating harder.', refIds: ['DIET-WHO'] },
  { text: 'Cutting back on sugar-sweetened drinks can support lower free-sugar intake.', refIds: ['DIET-WHO'] },
  { text: 'Choosing unsaturated fats over saturated fats supports WHO fat-quality guidance.', refIds: ['DIET-WHO'] },
  { text: 'Trimming visible fat from meat is one way WHO suggests reducing saturated fat intake.', refIds: ['DIET-WHO'] },
  { text: 'Steaming or boiling instead of frying can help reduce fat intake.', refIds: ['DIET-WHO'] },
  { text: 'Reduced-fat dairy foods can be one way to lower saturated fat intake.', refIds: ['DIET-WHO'] },
  { text: 'Nutrition labels can help people check nutrients such as fiber.', refIds: ['CARB-MLP', 'FIB-MLP'] },
  { text: 'Carbohydrate-rich foods include grains, fruit, beans, and milk.', refIds: ['CARB-MLP'] },
  { text: 'Beans and grains can contribute both carbohydrates and protein.', refIds: ['CARB-MLP', 'PRO-MLP'] },
  { text: 'Water needs vary by person and situation.', refIds: ['HYD-MLP'] },
  { text: 'People concerned about their water intake can discuss it with a health professional.', refIds: ['HYD-MLP'] },
  { text: 'Healthy diet guidance focuses on patterns over time, not on one meal.', refIds: ['DIET-WHO'] },
  { text: 'Nutrition needs can vary across life stages and health situations.', refIds: ['NUT-EFSA-DRV'] },
  { text: 'Dietary reference values are not one-size-fits-all meal plans.', refIds: ['NUT-EFSA-DRV'] },
];

export const FIT_FACT_ITEMS = [...ACTIVITY_FACTS, ...SLEEP_FACTS, ...NUTRITION_FACTS].map((fact, index) => ({
  id: `F${String(index + 1).padStart(3, '0')}`,
  ...fact,
}));

export const FIT_FACTS = FIT_FACT_ITEMS.map((fact) => fact.text);

export const HEALTH_SOURCE_REFERENCE_BY_ID = HEALTH_SOURCE_REFERENCES.reduce((accumulator, source) => {
  accumulator[source.id] = source;
  return accumulator;
}, {});
